import { Button } from "@groot/ui/button";
import { PageHeader } from "@groot/shell/components/layout";
import { Page } from "@/app/shared/components/page";
import { LoadingSpinner } from "@groot/ui/loading-spinner";
import { useDetailPage } from "@/hooks";
import { formatDetailedDateTime } from "@/lib/utils";
import { apiClient } from "@/services/api";
import { ArrowLeft, Trash2 } from "lucide-react";
import { useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";

export function MobileNotificationDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const fetchFn = useCallback(
    (id: string) => apiClient.getMobileNotificationById(Number.parseInt(id, 10)),
    [],
  );

  const deleteFn = useCallback(
    (id: string) => apiClient.deleteMobileNotification(Number.parseInt(id, 10)),
    [],
  );

  const {
    data: notification,
    loading,
    error,
    handleDelete,
  } = useDetailPage({
    fetchFn,
    deleteFn,
    id,
    redirectPath: "/mobile-notifications",
    resourceName: "notification",
  });

  if (loading) {
    return (
      <Page maxWidth="5xl">
        <LoadingSpinner size="lg" className="py-20" />
      </Page>
    );
  }

  if (error || !notification) {
    return (
      <Page maxWidth="5xl">
        <Button
          variant="ghost"
          onClick={() => navigate("/mobile-notifications")}
          className="mb-4 -ml-2"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Notifications
        </Button>
        <div className="text-center py-20">
          <p className="text-muted-foreground">{error || "Notification not found"}</p>
        </div>
      </Page>
    );
  }

  return (
    <Page maxWidth="5xl">
      {/* Header */}
      <Button
        variant="ghost"
        onClick={() => navigate("/mobile-notifications")}
        className="mb-4 -ml-2"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Notifications
      </Button>
      <PageHeader
        title={`Notification #${notification.id}`}
        description={`Posted ${formatDetailedDateTime(notification.createdAt)}`}
        actions={
          <Button variant="destructive" onClick={handleDelete}>
            <Trash2 className="size-4 mr-2" />
            Delete
          </Button>
        }
        className="mb-8"
      />

      <div className="space-y-8">
        {/* Details Section */}
        <div>
          <h2 className="text-sm font-medium text-foreground mb-4">Details</h2>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            <div>
              <dt className="text-sm text-muted-foreground">App Package</dt>
              <dd className="text-sm text-foreground mt-1">
                {notification.app_package || "Unknown"}
              </dd>
            </div>
            <div>
              <dt className="text-sm text-muted-foreground">Device</dt>
              <dd className="text-sm text-foreground mt-1">{notification.device || "Unknown"}</dd>
            </div>
            <div>
              <dt className="text-sm text-muted-foreground">Posted At</dt>
              <dd className="text-sm text-foreground mt-1">
                {formatDetailedDateTime(notification.createdAt)}
              </dd>
            </div>
          </dl>
        </div>

        {/* Notification Content */}
        <div className="pt-6 border-t border-border">
          <h2 className="text-sm font-medium text-foreground mb-4">Content</h2>
          {notification.title && (
            <div className="mb-3">
              <div className="text-xs text-muted-foreground mb-1">Title</div>
              <div className="text-sm font-medium text-foreground">{notification.title}</div>
            </div>
          )}
          {notification.text && (
            <div className="p-4 bg-muted rounded-lg border border-border">
              <p className="text-sm text-foreground whitespace-pre-wrap">{notification.text}</p>
            </div>
          )}
          {!notification.title && !notification.text && (
            <p className="text-sm text-muted-foreground">No notification content available</p>
          )}
        </div>

        {/* Metadata */}
        <div className="pt-6 border-t border-border">
          <h2 className="text-sm font-medium text-foreground mb-4">Metadata</h2>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            <div>
              <dt className="text-sm text-muted-foreground">Created At</dt>
              <dd className="text-sm text-foreground mt-1">
                {formatDetailedDateTime(notification.createdAt)}
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </Page>
  );
}
